import React from "react";
import "./PrivacyPolicy.css";
import Navbar from "../Include/Navbar";
import Footer from "../Include/Footer";

function PrivacyPolicyExpenseTracker() {
 return (
    <>
<div className="privacy-page">
  <div className="container">
    <h1>Privacy Policy</h1>

    <p>
      Innosutra AI Private Limited (“Company”, “Innosutra”, “we”, “our”, or “us”) values the privacy of
      individuals (“you”, “your”, “user”) who access and use our mobile application “Expense Tracker” (the
      “Service”). The Service is a personal finance app that helps you record expenses, income and
      investments, plan budgets and savings goals, track recurring bills, and keep reminders of money you
      owe or are owed. This Privacy Policy explains what information the Service collects, how it is used,
      and the choices you have. It forms part of our Terms of Use (“ToU”); undefined capitalized terms have
      the meaning given in the ToU. By using the Service, you agree to this Policy. If you do not agree,
      please do not use the Service.
    </p>

    <section className="section">
      <h2>1. Updates to This Policy</h2>
      <p>
        We may revise this Privacy Policy to reflect changes in the Service, our practices, or legal
        requirements.
      </p>
      <ul>
        <li>The updated Policy will be posted within the Service and/or on our website, with a revised “last updated” date.</li>
        <li>Material changes will be highlighted through an in-app notice where practicable.</li>
        <li>Continued use of the Service after an update constitutes acceptance of the revised Policy.</li>
      </ul>
    </section>

    <section className="section">
      <h2>2. Information We Collect</h2>

      <p>a) Account Information</p>
      <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Password (stored in encrypted/hashed form — we cannot see your actual password)</li>
        <li>Profile photo, if you choose to upload one</li>
      </ul>

      <p>b) Financial Information You Enter</p>
      <ul>
        <li>Transactions: amount, type (expense, income, or investment), category, note, date, and payment method (cash, UPI, card, or bank)</li>
        <li>Custom categories and category budgets</li>
        <li>Savings goals and their progress</li>
        <li>Recurring entries (e.g. rent, salary, SIPs) and bill reminders</li>
        <li>Money reminders you create to track who owes you money, or who you owe (person’s name, amount, direction, date, and an optional note)</li>
        <li>Your profile’s currency and monthly budget settings</li>
      </ul>

      <p>c) Multiple Profiles</p>
      <p>
        The Service lets one account maintain separate profiles (for example, to track a family member’s
        finances separately). Each profile has its own name, optional icon/photo, and financial data as
        described above.
      </p>

      <p>d) Device and Usage Information</p>
      <ul>
        <li>Device identifiers, device model, and operating system version</li>
        <li>App version and general usage/diagnostic information, used to maintain and improve the Service</li>
        <li>A push-notification token (see Section 6)</li>
      </ul>

      <p>
        We do not knowingly collect sensitive categories of personal data such as government identification
        numbers, health or medical information, or biometric data. Please do not enter such information into
        note fields or anywhere else in the Service.
      </p>
    </section>

    <section className="section">
      <h2>3. SMS Access (Optional, Permission-Based)</h2>
      <p>
        With your explicit permission, the Service can read transactional SMS messages (such as bank and
        UPI payment alerts) already on your device, so you don’t have to type every expense in by hand.
        Here is exactly how this works:
      </p>
      <ul>
        <li>SMS access is requested only after you opt in from within the app; you can decline, and the rest of the Service works without it.</li>
        <li>Message text is scanned and parsed <strong>on your device</strong> to detect likely bank/UPI transaction alerts. We do not upload the full text of your SMS messages to our servers.</li>
        <li>Detected candidates (amount, merchant, date, suggested category) are shown to you for review — nothing is saved to your account until you confirm or edit and accept an entry.</li>
        <li>Only the transaction details you approve — not the original SMS content — are stored as a normal expense/income entry in your account.</li>
        <li>You can revoke SMS permission at any time from your device’s Settings, which stops this feature without affecting the rest of the Service.</li>
      </ul>
    </section>

    <section className="section">
      <h2>4. Biometric App Lock (On-Device Only)</h2>
      <p>
        The Service can use your device’s built-in fingerprint, face unlock, or screen-lock PIN to (a)
        optionally lock the app itself, and (b) confirm it’s you before revealing masked amounts on the
        dashboard.
      </p>
      <ul>
        <li>This authentication is performed entirely by your device’s operating system.</li>
        <li>We never receive, see, transmit, or store your fingerprint, face data, or any other biometric information — the app only receives a yes/no confirmation from the OS.</li>
        <li>This feature is optional and can be turned off at any time in the app’s Settings.</li>
      </ul>
    </section>

    <section className="section">
      <h2>5. Microphone (Voice Entry, Optional)</h2>
      <p>
        If you use the voice-entry shortcut to add an expense by speaking instead of typing, the Service
        uses your device’s microphone and speech-recognition capability to convert your speech to text. We
        do not store audio recordings; only the resulting text (and the entry you choose to save) is
        retained.
      </p>
    </section>

    <section className="section">
      <h2>6. Push Notifications</h2>
      <p>
        If you enable notifications, we use Firebase Cloud Messaging to deliver reminders (such as upcoming
        bills or recurring entries) to your device. This requires storing a device notification token, which
        is used solely to deliver these notifications and can be disabled at any time in Settings or your
        device’s notification permissions.
      </p>
    </section>

    <section className="section">
      <h2>7. How We Use Your Information</h2>
      <ul>
        <li>To create and secure your account, and authenticate you when you sign in</li>
        <li>To provide the core Service: recording transactions, tracking budgets, savings goals, recurring bills, and money reminders</li>
        <li>To generate the summaries, charts, and monthly reports shown in the app</li>
        <li>To detect likely transactions from SMS you’ve permitted us to read (Section 3)</li>
        <li>To send reminders and notifications you’ve opted into</li>
        <li>To process subscription payments via our payment partner (Section 9)</li>
        <li>To provide customer support and respond to your requests</li>
        <li>To maintain the security, integrity, and reliability of the Service and prevent misuse</li>
        <li>To comply with legal obligations</li>
      </ul>
      <p>We do not use your financial data to show you third-party advertising.</p>
    </section>

    <section className="section">
      <h2>8. Sharing Your Information</h2>
      <p>We do not sell your personal or financial information. We only share information with:</p>
      <ul>
        <li>
          <strong>Payment processors</strong> — subscription payments are handled by Razorpay. We do not
          store your card, UPI, or bank credentials; that information is collected and processed directly by
          Razorpay under its own privacy policy.
        </li>
        <li>
          <strong>Infrastructure and messaging providers</strong> — such as our cloud hosting/database
          provider and Firebase Cloud Messaging (for notifications), who process data only on our
          instructions and solely to provide these services.
        </li>
        <li><strong>Legal and safety reasons</strong> — if required by law, legal process, or to protect the rights, safety, or property of Innosutra, our users, or the public.</li>
        <li><strong>Business transfers</strong> — if we are involved in a merger, acquisition, or asset sale, your information may be transferred, subject to this Policy.</li>
      </ul>
    </section>

    <section className="section">
      <h2>9. Third-Party Services We Use</h2>
      <ul>
        <li><strong>Razorpay</strong> — subscription billing and payment processing.</li>
        <li><strong>Firebase Cloud Messaging</strong> — delivering push notifications.</li>
        <li>A third-party financial news provider — to display news articles in the app; this does not involve sharing your personal or financial data.</li>
      </ul>
      <p>Each of these providers has its own privacy policy governing how it handles data.</p>
    </section>

    <section className="section">
      <h2>10. Data Security</h2>
      <p>
        We use industry-standard safeguards to protect your information, including encrypted transmission
        (HTTPS) between the app and our servers, encryption of sensitive financial figures at rest where
        applicable, hashed password storage, and access controls on our systems. No method of transmission
        or storage is 100% secure, but we work to protect your information using commercially reasonable
        measures.
      </p>
    </section>

    <section className="section">
      <h2>11. Data Retention</h2>
      <p>
        We retain your account and financial data for as long as your account remains active, so that the
        Service can continue to function (e.g. showing historical reports). If you delete a specific entry,
        profile, or your account, we delete or anonymize the corresponding data within a reasonable period,
        except where we are required to retain it to comply with legal, tax, or accounting obligations.
      </p>
    </section>

    <section className="section">
      <h2>12. Your Rights and Choices</h2>
      <ul>
        <li>You can view, edit, or delete individual transactions, categories, budgets, goals, bill reminders, money reminders, and profiles at any time within the app.</li>
        <li>You can export your transaction history as a CSV file or a PDF report at any time.</li>
        <li>You can disable SMS access, biometric lock, voice entry, or push notifications at any time, without losing access to the rest of the Service.</li>
        <li>You can request a copy of, correction to, or deletion of your personal data by contacting us (Section 14).</li>
      </ul>
    </section>

    <section className="section">
      <h2>13. Account and Data Deletion</h2>
      <p>
        You can delete individual profiles and data from within the app. To request deletion of your entire
        account and all associated data, email us at{' '}
        <a href="mailto:innosutraaiprivatelimited@gmail.com">innosutraaiprivatelimited@gmail.com</a> from your registered email address
        with the subject “Account Deletion Request.” We will verify your request and delete your account and
        personal data within 30 days, except where we must retain certain records to meet legal or
        regulatory obligations.
      </p>
    </section>

    <section className="section">
      <h2>14. Children’s Privacy</h2>
      <p>
        The Service is intended for users who are at least 18 years old. We do not knowingly collect
        personal information from children. If you believe a child has provided us with personal
        information, please contact us so we can delete it.
      </p>
    </section>

    <section className="section">
      <h2>15. Grievance Officer / Contact Us</h2>
      <p>
        If you have questions, concerns, or requests regarding this Privacy Policy or your data, please
        contact:
      </p>
      <p>
        Innosutra AI Private Limited
        <br />
        Email: <a href="mailto:innosutraaiprivatelimited@gmail.com">innosutraaiprivatelimited@gmail.com</a>
      </p>
    </section>

    <section className="section">
      <h2>16. Governing Law</h2>
      <p>
        This Privacy Policy is governed by the laws of India. Any disputes arising from this Policy will be
        subject to the exclusive jurisdiction of the courts located in India.
      </p>
    </section>
  </div>
</div>
    </>
  );
}

export default PrivacyPolicyExpenseTracker;